import React, { useMemo } from 'react';
import { Avatar, Message } from '@chatscope/chat-ui-kit-react';
import moment from 'moment';
import { LoadedAdminData } from 'client/admin/shared/types';
import { UserAvatar } from 'client/admin/shared/components/user-avatar';
import {
  AiAssistantMessage,
  AiAssistantMessageRole,
} from 'client/admin/core/ai-assistant-session';
import { MaterialIcon, MaterialIconName } from 'client/shared/components/base';
import './styles.scss';
import pollyParrotImage from 'client/assets/polly-parrot.svg';
import { MarkdownWithEmbeddedContent } from './embedded-content';
import { AiAssistant, AiAssistantName } from 'core';
import { useTyper } from '../../hooks/use-typer';

const baseClass = 'pn-ai-assistant-message';
const ASSISTANT_NAME = 'Polly';

export const AiAssistantMessageComponent: React.FC<{
  readonly adminData: LoadedAdminData;
  readonly assistant: AiAssistant;
  readonly className?: string;
  readonly aiAssistantMessage: AiAssistantMessage;
  readonly messageIndex: number;
  readonly sessionId: string;
}> = (p) => {
  const sender = getSender(p.aiAssistantMessage.role, p.adminData);

  // Ideally our state would differentiate messages added during this page view,
  // but it doesn't, and that'll be relatively complicated.
  // So for now, make a best guess based on the timestamp of the message.
  const isNewMessage = useMemo(
    () => p.aiAssistantMessage.timestamp.getTime() > Date.now() - 3000,
    // Only compute this once, so it doesn't refresh while typing the message
    // (during which time the message is getting older) thus suddenly stopping
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  // Ideally we'd stream more real-time updates from the API, but that's a lot of work
  const typedText = useTyper({
    targetText: p.aiAssistantMessage.content,
    patternsToInstantlyType: [/^<EmbeddedComponent.*\/>/],
    msPerChar: 8,
    skip:
      p.messageIndex === 0 ||
      p.aiAssistantMessage.role !== AiAssistantMessageRole.ASSISTANT ||
      !isNewMessage ||
      process.env.NODE_ENV === 'test',
  });

  return (
    <Message
      avatarPosition={getAvatarPosition(p.aiAssistantMessage.role)}
      className={`${baseClass} ${p.className ?? ''} ${
        p.aiAssistantMessage.role === AiAssistantMessageRole.SYSTEM
          ? 'system-message'
          : ''
      }`}
      model={{
        sender,
        sentTime: moment(p.aiAssistantMessage.timestamp).fromNow(),
        direction: getMessageDirection(p.aiAssistantMessage.role),
        position: 'single',
        payload: (
          <Message.CustomContent>
            <MarkdownWithEmbeddedContent
              assistant={p.assistant}
              className={`${baseClass}-markdown`}
              content={typedText}
              fipsId={p.adminData.admin.activePublishingEntity.fips ?? undefined}
              pubId={p.adminData.admin.activePublishingEntity.id}
              sessionId={p.sessionId}
            />
          </Message.CustomContent>
        ),
      }}
      type={'custom'}
    >
      {/* Avatar must be the only child */}
      <Avatar name={sender} size={'sm'}>
        <CustomAvatar
          adminData={p.adminData}
          assistant={p.assistant}
          role={p.aiAssistantMessage.role}
        />
      </Avatar>
    </Message>
  );
};

export const CustomAvatar: React.FC<{
  readonly adminData: LoadedAdminData;
  readonly assistant: AiAssistant;
  readonly role: AiAssistantMessageRole;
}> = ({ adminData, assistant, role }) => {
  switch (role) {
    case AiAssistantMessageRole.ASSISTANT:
      switch (assistant.id) {
        case AiAssistantName.POLLY:
          return <img alt="polly avatar" src={pollyParrotImage} />;
        case AiAssistantName.GRANT_WRITER:
          return <UserAvatar name="Grace" />;
        case AiAssistantName.ANNIE:
          return <UserAvatar name="Annie" />;
        case AiAssistantName.SANDBOX:
        default:
          return <UserAvatar name="Assistant" />;
      }
    case AiAssistantMessageRole.USER:
      return <UserAvatar name={adminData.admin.name} />;
    case AiAssistantMessageRole.SYSTEM:
      return (
        <MaterialIcon
          className="text-grayscale-4"
          icon={MaterialIconName.INFO}
          iconType={'material-icons-outlined'}
        />
      );
    case AiAssistantMessageRole.FILE_UPLOAD:
      return (
        <MaterialIcon
          className="text-grayscale-4"
          icon={MaterialIconName.ATTACH_FILE}
          iconType={'material-icons-outlined'}
        />
      );
  }
};

function getSender(role: AiAssistantMessageRole, adminData: LoadedAdminData) {
  switch (role) {
    case AiAssistantMessageRole.ASSISTANT:
      return ASSISTANT_NAME;
    case AiAssistantMessageRole.USER:
      return adminData.admin.name;
    default:
      return 'System';
  }
}

function getAvatarPosition(role: AiAssistantMessageRole) {
  switch (role) {
    case AiAssistantMessageRole.USER:
      return 'tr';
    default:
      return 'tl';
  }
}

function getMessageDirection(role: AiAssistantMessageRole) {
  switch (role) {
    case AiAssistantMessageRole.USER:
      return 'outgoing';
    default:
      return 'incoming';
  }
}
